<template>
  <div class="pro-tip-requests">
    <div class="back-link-section">
      <router-link :to="`/${user.username}`" class="back-link">
        <span class="arrow">&#8592;</span>
        <span class="back-link-text">Back to Profile</span>
      </router-link>
    </div>

    <!-- Toggle buttons for switching views -->
    <div class="view-toggle">
      <button 
        @click="activeView = 'requests'" 
        :class="['toggle-btn', activeView === 'requests' ? 'active' : '']"
      >
        Pro Tip Requests
      </button>
      <button 
        @click="activeView = 'overview'" 
        :class="['toggle-btn', activeView === 'overview' ? 'active' : '']"
      >
        Payment Overview
      </button>
      <button 
        @click="activeView = 'payouts'" 
        :class="['toggle-btn', activeView === 'payouts' ? 'active' : '']"
      >
        Payouts
      </button>
    </div>

    <!-- Stripe Connect Setup Banner -->
    <div v-if="shouldShowConnectBanner" class="connect-banner">
      <div class="connect-banner-content">
        <div class="connect-banner-icon">
          <i class="fas fa-credit-card"></i>
        </div>
        <div class="connect-banner-text">
          <h4>Set up payouts to receive your earnings</h4>
          <p>You have pending tip requests! Set up your payout method to automatically receive payments when you submit tips.</p>
        </div>
        <div class="connect-banner-actions">
          <button 
            @click="setupStripeConnect" 
            class="connect-setup-btn"
            :disabled="connectLoading"
          >
            <span v-if="connectLoading">Setting up...</span>
            <span v-else>Set up payouts</span>
          </button>
          <button @click="dismissConnectBanner" class="connect-dismiss-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Pro Tip Requests View -->
    <div v-if="activeView === 'requests'">
      <h2 class="page-title">Pro Tip Requests</h2>

      <LoadingSpinner v-if="loading" />
      <div v-else>
        <p v-if="tipRequests.length === 0" class="no-requests-message">
          No tip requests yet.
        </p>
        <ul v-else class="tip-requests-list">
          <li v-for="request in tipRequests" :key="request.id" class="request-item">
            <!-- User Info and Payment Details -->
            <div class="first-row">
              <div class="user-info">
                <img
                  :src="request.pro_avatar || defaultAvatar"
                  class="user-avatar"
                  alt="User Avatar"
                />
                <span class="username">
                  {{ request.user_username }}
                </span>
              </div>
              <div class="payment-details">
                <div class="payment-status">
                  <strong>Status:</strong> {{ request.status }}
                </div>
                <div class="payment-price">
                  <strong>Price:</strong> {{ currencySymbol }}{{ calculateNetAmount(request) }}
                </div>
              </div>
            </div>

            <!-- Post Preview with Full-Size Video -->
            <div class="post-content" v-if="request.post_preview">
              <img 
                v-if="request.post_preview.image" 
                :src="request.post_preview.image" 
                alt="Post image" 
                class="post-media"
              />
              <div class="video-container" v-if="request.post_preview.video">
                <video
                  :src="request.post_preview.video"
                  muted
                  autoplay
                  loop
                  playsinline
                  class="post-media"
                ></video>
              </div>
              <p class="post-text">{{ request.post_preview.content }}</p>
            </div>

            <!-- Add Tip Form -->
            <div v-if="request.status === 'pending'" class="add-tip-form">
              <!-- Pro Tips Information Section -->
              <div class="pro-tips-info">
                <div class="info-header" @click="toggleInfo(request.id)">
                  <div class="info-left">
                    <span class="info-text">Information about Pro Tips</span>
                    <i class="fas fa-info-circle info-icon" :class="{ 'active': showInfo[request.id] }"></i>
                  </div>
                  <div class="character-counter">
                    {{ (newTips[request.id] || '').length }}
                  </div>
                </div>
                <div v-if="showInfo[request.id]" class="info-content">
                  <ul class="info-list">
                    <li>Submit your tip within 7 days to capture the payment - after this period, the payment will be automatically refunded</li>
                    <li>Once you submit your tip, the payment will be immediately captured and processed for payout</li>
                    <li>The first 200 characters of your tip are visible to all users as a preview, while the complete tip content is exclusively available to the paying customer</li>
                    <li>Provide detailed, actionable advice to maximize value for the customer and enhance your reputation as a coach</li>
                  </ul>
                </div>
              </div>
              
              <textarea 
                v-model="newTips[request.id]" 
                placeholder="Write your paid coaching tip here... Please provide your best effort to make this tip as helpful as possible."
                class="tip-textarea"
              ></textarea>
              <button 
                @click="submitTip(request.id)" 
                class="submit-tip-btn"
                :disabled="!newTips[request.id] || (newTips[request.id] || '').length < 80"
                :title="(!newTips[request.id] || (newTips[request.id] || '').length < 80) ? 'You need to write at least 80 characters' : ''"
              >
                Submit Tip
              </button>
            </div>
          </li>
        </ul>
      </div>
    </div>

    <!-- Payment Overview View -->
    <div v-else-if="activeView === 'overview'">
      <h2 class="page-title">Payment Overview</h2>
      
      <LoadingSpinner v-if="loading" />
      <div v-else class="payment-overview">
        <div class="payment-stats">
          <div class="stat-card">
            <h3>Total Earnings</h3>
            <p class="stat-value">{{ currencySymbol }}{{ totalNetEarnings.toFixed(2) }}</p>
          </div>
          <div class="stat-card">
            <h3>Completed Tips</h3>
            <p class="stat-value">{{ completedTips.length }}</p>
          </div>
          <div class="stat-card">
            <h3>Pending Tips</h3>
            <p class="stat-value">{{ pendingTips.length }}</p>
          </div>
        </div>
        
        <div class="chart-container">
          <h3>Earnings Over Time</h3>
          <canvas ref="earningsChart" width="400" height="200"></canvas>
        </div>
        
        <div class="recent-payments">
          <h3>Recent Payments</h3>
          <div v-if="completedTips.length === 0" class="no-data">
            No completed payments to display
          </div>
          <table v-else class="payments-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>User</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="payment in completedTips" :key="payment.id">
                <td>{{ formatDate(payment.created_at) }}</td>
                <td>{{ payment.user_username }}</td>
                <td>{{ currencySymbol }}{{ calculateNetAmount(payment) }}</td>
                <td>{{ payment.status }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Payouts View -->
    <div v-else-if="activeView === 'payouts'">
      <h2 class="page-title">Payouts</h2>
      
      <LoadingSpinner v-if="payoutLoading" />
      <div v-else class="payouts-overview">
        
        <!-- Payout Status Cards -->
        <div class="payout-status-cards">
          <div class="status-card pending-card">
            <div class="card-header">
              <h3>Pending Payouts</h3>
              <i class="fas fa-clock"></i>
            </div>
            <div class="card-content">
              <div class="amount">{{ currencySymbol }}{{ payoutData.pendingAmount.toFixed(2) }}</div>
              <div class="count">{{ payoutData.pendingPaymentsCount }} payment{{ payoutData.pendingPaymentsCount !== 1 ? 's' : '' }}</div>
            </div>
          </div>
          
          <div class="status-card transferred-card">
            <div class="card-header">
              <h3>Transferred</h3>
              <i class="fas fa-check-circle"></i>
            </div>
            <div class="card-content">
              <div class="amount">{{ currencySymbol }}{{ (totalNetEarnings - payoutData.pendingAmount).toFixed(2) }}</div>
              <div class="count">{{ completedTips.length - payoutData.pendingPaymentsCount }} payment{{ (completedTips.length - payoutData.pendingPaymentsCount) !== 1 ? 's' : '' }}</div>
            </div>
          </div>
        </div>

        <!-- Connect Account Status -->
        <div class="connect-status-section">
          <h3>Payout Account Status</h3>
          
          <div v-if="!payoutData.hasConnectAccount" class="connect-status connect-missing">
            <div class="status-icon">
              <i class="fas fa-exclamation-triangle"></i>
            </div>
            <div class="status-content">
              <div class="status-title">No Payout Account</div>
              <div class="status-description">Set up your payout account to receive earnings automatically.</div>
            </div>
            <button @click="setupStripeConnect" class="setup-connect-btn" :disabled="connectLoading">
              <span v-if="connectLoading">Setting up...</span>
              <span v-else>Set up payouts</span>
            </button>
          </div>
          
          <div v-else-if="!payoutData.connectReady" class="connect-status connect-pending">
            <div class="status-icon">
              <i class="fas fa-clock"></i>
            </div>
            <div class="status-content">
              <div class="status-title">Setup In Progress</div>
              <div class="status-description">Your payout account is being verified. This usually takes a few minutes.</div>
            </div>
            <button @click="checkConnectAccountStatus" class="refresh-btn">
              <i class="fas fa-sync-alt"></i> Check Status
            </button>
          </div>
          
          <div v-else class="connect-status connect-ready">
            <div class="status-icon">
              <i class="fas fa-check-circle"></i>
            </div>
            <div class="status-content">
              <div class="status-title">Payouts Enabled</div>
              <div class="status-description">Your account is ready to receive automatic payouts.</div>
            </div>
            <div class="status-badge">Ready</div>
          </div>
        </div>

        <!-- Pending Earnings Info -->
        <div v-if="payoutData.pendingAmount > 0" class="pending-earnings-info">
          <div class="info-header">
            <h3>About Your Pending Earnings</h3>
          </div>
          <div class="info-content">
            <div class="info-item">
              <i class="fas fa-info-circle"></i>
              <span v-if="!payoutData.connectReady">
                Complete your payout setup to receive {{ currencySymbol }}{{ payoutData.pendingAmount.toFixed(2) }} in pending earnings.
              </span>
              <span v-else>
                {{ currencySymbol }}{{ payoutData.pendingAmount.toFixed(2) }} will be transferred to your account automatically.
              </span>
            </div>
            <div class="info-item">
              <i class="fas fa-clock"></i>
              <span>Payouts are processed within 1-3 business days once your account is verified.</span>
            </div>
          </div>
        </div>

        <!-- No Earnings State -->
        <div v-if="payoutData.pendingAmount === 0 && totalNetEarnings === 0" class="no-earnings">
          <div class="no-earnings-icon">
            <i class="fas fa-piggy-bank"></i>
          </div>
          <h3>No Earnings Yet</h3>
          <p>Start receiving tip requests to begin earning money from your expertise!</p>
        </div>

      </div>
    </div>

    <!-- Country Selection Modal -->
    <div v-if="showCountryModal" class="modal-overlay" @click="closeCountryModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Select Your Country</h3>
          <button @click="closeCountryModal" class="modal-close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <p class="modal-description">
            Please select the country where you want to receive payouts. This will determine which banking options are available to you.
          </p>
          
          <div v-if="countriesLoading" class="loading-countries">
            Loading countries...
          </div>
          
          <div v-else>
            <!-- Search input -->
            <div class="country-search">
              <input 
                v-model="countrySearchQuery"
                type="text"
                placeholder="Search countries..."
                class="search-input"
              />
            </div>
            
            <div class="countries-list">
              <div 
                v-for="country in filteredCountries" 
                :key="country.code"
                @click="selectCountry(country.code)"
                :class="['country-item', selectedCountry === country.code ? 'selected' : '']"
              >
                <span class="country-name">{{ country.name }}</span>
                <span class="country-code">{{ country.code }}</span>
              </div>
              
              <div v-if="filteredCountries.length === 0" class="no-countries">
                No countries found matching "{{ countrySearchQuery }}"
              </div>
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <button @click="closeCountryModal" class="modal-cancel-btn">
            Cancel
          </button>
          <button 
            @click="createConnectAccountWithCountry" 
            :disabled="!selectedCountry || connectLoading"
            class="modal-confirm-btn"
          >
            <span v-if="connectLoading">Creating account...</span>
            <span v-else>Continue with {{ selectedCountry }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { API_BASE_URL } from '@/config';
import axios from "axios";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import Chart from 'chart.js/auto';

export default {
  name: "ProTipRequests",
  components: {
    LoadingSpinner
  },
  data() {
    return {
      activeView: 'requests', // Default view
      tipRequests: [],
      loading: true,
      newTips: {},
      showInfo: {},
      defaultAvatar: "https://6buoys-bucket.s3.amazonaws.com/avatar.png",
      user: {},
      isAuthenticated: false,
      earningsChart: null,
      // Add Stripe Connect related data
      connectAccountStatus: {
        hasAccount: false,
        onboardingComplete: false,
        payoutsEnabled: false,
        needsSetup: false
      },
      connectLoading: false,
      showConnectBanner: false,
      // Add country selection data
      supportedCountries: [],
      selectedCountry: '',
      showCountryModal: false,
      countriesLoading: false,
      countrySearchQuery: '',
      // Add payout data
      payoutData: {
        pendingAmount: 0,
        pendingPaymentsCount: 0,
        hasConnectAccount: false,
        connectReady: false
      },
      payoutLoading: false
    };
  },
  computed: {
    currencySymbol() {
      const symbols = { EUR: '€', USD: '$', GBP: '£' };
      return symbols[(this.user.currency || 'EUR').toUpperCase()] || '€';
    },
    completedTips() {
      return this.tipRequests.filter(request => request.status === 'captured');
    },
    pendingTips() {
      return this.tipRequests.filter(request => request.status === 'pending');
    },
    totalNetEarnings() {
      return this.completedTips.reduce((total, payment) => {
        const amount = parseFloat(payment.amount);
        const serviceFee = parseFloat(payment.service_fee || 0);
        return total + (amount - serviceFee);
      }, 0);
    },
    // Add computed property for Connect banner visibility
    shouldShowConnectBanner() {
      // Show banner if user has tip requests but no payout method set up
      return this.tipRequests.length > 0 && 
             this.connectAccountStatus.needsSetup && 
             !this.connectAccountStatus.payoutsEnabled;
    },
    filteredCountries() {
      if (!this.countrySearchQuery) {
        return this.supportedCountries;
      }
      
      const query = this.countrySearchQuery.toLowerCase();
      return this.supportedCountries.filter(country => 
        country.name.toLowerCase().includes(query) || 
        country.code.toLowerCase().includes(query)
      );
    },
    monthlyEarnings() {
      // Group payments by month and calculate total for each month
      const earnings = {};
      
      this.completedTips.forEach(payment => {
        const date = new Date(payment.created_at);
        const monthYear = `${date.toLocaleString('default', { month: 'short' })} ${date.getFullYear()}`;
        
        if (!earnings[monthYear]) {
          earnings[monthYear] = {
            gross: 0,
            net: 0
          };
        }
        
        const amount = parseFloat(payment.amount);
        const serviceFee = parseFloat(payment.service_fee || 0);
        earnings[monthYear].gross += amount;
        earnings[monthYear].net += (amount - serviceFee);
      });
      
      // Convert to array sorted by date
      return Object.entries(earnings)
        .map(([month, amounts]) => ({ 
          month, 
          amount: amounts.gross,
          netAmount: amounts.net 
        }))
        .sort((a, b) => {
          const [aMonth, aYear] = a.month.split(' ');
          const [bMonth, bYear] = b.month.split(' ');
          
          if (aYear !== bYear) return aYear - bYear;
          
          const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          return months.indexOf(aMonth) - months.indexOf(bMonth);
        });
    }
  },
  async created() {
    await this.checkAuthentication();
    if (this.isAuthenticated && this.user.verified) {
      await this.fetchTipRequests();
      // Check Stripe Connect status after fetching tip requests
      await this.checkConnectAccountStatus();
    } else {
      // Redirect non-verified users
      this.$router.push('/profile');
    }
  },
  watch: {
    activeView(newView) {
      if (newView === 'overview') {
        this.$nextTick(() => {
          this.renderEarningsChart();
        });
      } else if (newView === 'payouts') {
        this.fetchPayoutData();
      }
    }
  },
  methods: {
    async checkAuthentication() {
      const token = localStorage.getItem("access_token");
      if (token) {
        try {
          const response = await axios.get(`${API_BASE_URL}/api/profile/`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          this.user = response.data;
          this.isAuthenticated = true;
        } catch (error) {
          console.error("Error fetching user data:", error);
          this.isAuthenticated = false;
          this.$router.push('/login');
        }
      } else {
        this.isAuthenticated = false;
        this.$router.push('/login');
      }
    },
    async fetchTipRequests() {
      this.loading = true;
      try {
        const token = localStorage.getItem("access_token");
        const response = await axios.get(
          `${API_BASE_URL}/payments/pro-payments/${this.user.user_id}/`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        this.tipRequests = response.data;
      } catch (error) {
        console.error("Error fetching tip requests:", error);
      } finally {
        this.loading = false;
      }
    },
    async submitTip(requestId) {
      if (!this.isAuthenticated) {
        alert("Please log in to submit a tip.");
        return;
      }

      const tipText = this.newTips[requestId]?.trim();
      if (!tipText) {
        alert("Please enter a valid tip.");
        return;
      }

      try {
        const token = localStorage.getItem("access_token");
        
        // First, submit the tip
        const tipResponse = await axios.post(
          `${API_BASE_URL}/api/comment-create/`,
          {
            post: this.tipRequests.find(r => r.id === requestId)?.post_preview?.id,
            body: tipText,
            payment: requestId,
            pro_tip: true
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        // If tip is submitted successfully, capture the payment
        if (tipResponse.status === 201 || tipResponse.status === 200) {
          // Get the payment intent ID from the request
          const request = this.tipRequests.find(r => r.id === requestId);
          const paymentIntentId = request.stripe_payment_intent_id;

          // Capture the payment
          const captureResponse = await axios.post(
            `${API_BASE_URL}/payments/capture-intent/`,
            {
              payment_id: paymentIntentId
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (captureResponse.data.success) {
            // Clear the input and update UI - using Vue's reactivity instead of $set
            this.newTips = {
              ...this.newTips,
              [requestId]: ''
            };
            
            // Reload tip requests to get updated data
            await this.fetchTipRequests();

            // Show success message
            alert("Tip submitted and payment captured successfully!");
            console.log("Tip submitted and payment captured successfully!");
            return; // Exit the function after successful completion
          }
        }
        
        
      } catch (error) {
        // Only handle actual errors
        if (error.response) {
          const errorMessage = error.response.data?.error || error.response.data?.message || "Failed to submit tip. Please try again.";
          console.error("Error submitting tip:", error);
          alert(errorMessage);
        } else {
          console.error("Error submitting tip:", error);
          alert("An unexpected error occurred. Please try again.");
        }
      }
    },
    calculateNetAmount(request) {
      const amount = parseFloat(request.amount);
      const serviceFee = parseFloat(request.service_fee || 0);
      return (amount - serviceFee).toFixed(2);
    },
    renderEarningsChart() {
      if (!this.$refs.earningsChart) return;
      
      // Destroy existing chart if it exists
      if (this.earningsChart) {
        this.earningsChart.destroy();
      }
      
      const ctx = this.$refs.earningsChart.getContext('2d');
      
      // If no data, show empty chart
      if (this.monthlyEarnings.length === 0) {
        this.earningsChart = new Chart(ctx, {
          type: 'line',
          data: {
            labels: ['No data available'],
            datasets: [{
              label: `Monthly Earnings (${this.currencySymbol})`,
              data: [0],
              backgroundColor: 'rgba(29, 161, 242, 0.2)',
              borderColor: 'rgba(29, 161, 242, 1)',
              borderWidth: 2
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: {
                beginAtZero: true,
                ticks: { color: '#d9d9d9' },
                grid: { color: 'rgba(255, 255, 255, 0.1)' }
              },
              x: {
                ticks: { color: '#d9d9d9' },
                grid: { color: 'rgba(255, 255, 255, 0.1)' }
              }
            },
            plugins: {
              legend: { labels: { color: '#d9d9d9' } }
            }
          }
        });
        return;
      }
      
      // Format data for chart - use net earnings instead of gross
      const labels = this.monthlyEarnings.map(item => item.month);
      const data = this.monthlyEarnings.map(item => item.netAmount);
      
      this.earningsChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: labels,
          datasets: [{
            label: `Monthly Earnings (${this.currencySymbol})`,
            data: data,
            backgroundColor: 'rgba(29, 161, 242, 0.2)',
            borderColor: 'rgba(29, 161, 242, 1)',
            borderWidth: 2,
            tension: 0.4,
            fill: true
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              beginAtZero: true,
              ticks: { color: '#d9d9d9' },
              grid: { color: 'rgba(255, 255, 255, 0.1)' }
            },
            x: {
              ticks: { color: '#d9d9d9' },
              grid: { color: 'rgba(255, 255, 255, 0.1)' }
            }
          },
          plugins: {
            legend: { labels: { color: '#d9d9d9' } }
          }
        }
      });
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },
    toggleInfo(requestId) {
      this.showInfo = {
        ...this.showInfo,
        [requestId]: !this.showInfo[requestId]
      };
    },
    async checkConnectAccountStatus() {
      try {
        const token = localStorage.getItem("access_token");
        const response = await axios.get(
          `${API_BASE_URL}/payments/connect/account-status/`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        
        // Update status based on response
        this.connectAccountStatus = {
          hasAccount: !!response.data.account_id,
          onboardingComplete: response.data.details_submitted || false,
          payoutsEnabled: response.data.payouts_enabled || false,
          needsSetup: !response.data.payouts_enabled
        };
        
      } catch (error) {
        console.error("Error checking Connect account status:", error);
        // If no account exists, user needs setup
        if (error.response && error.response.status === 400) {
          this.connectAccountStatus = {
            hasAccount: false,
            onboardingComplete: false,
            payoutsEnabled: false,
            needsSetup: true
          };
        }
      }
    },
    async fetchSupportedCountries() {
      if (this.supportedCountries.length > 0) return; // Already loaded
      
      this.countriesLoading = true;
      try {
        const token = localStorage.getItem("access_token");
        const response = await axios.get(
          `${API_BASE_URL}/payments/connect/supported-countries/`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        this.supportedCountries = response.data.countries || [];
        
        // Pre-select user's country if available and supported
        if (this.user.country && this.supportedCountries.length > 0) {
          const userCountryCode = this.mapCountryNameToCode(this.user.country);
          const isSupported = this.supportedCountries.some(c => c.code === userCountryCode);
          if (isSupported) {
            this.selectedCountry = userCountryCode;
          }
        }
        
      } catch (error) {
        console.error("Error fetching supported countries:", error);
        alert("Failed to load supported countries. Please try again.");
      } finally {
        this.countriesLoading = false;
      }
    },
    mapCountryNameToCode(countryName) {
      // Map common country names to their ISO codes
      const countryMapping = {
        'Netherlands': 'NL',
        'Germany': 'DE', 
        'France': 'FR',
        'Spain': 'ES',
        'Italy': 'IT',
        'Belgium': 'BE',
        'Austria': 'AT',
        'Switzerland': 'CH',
        'United Kingdom': 'GB',
        'Ireland': 'IE',
        'Portugal': 'PT',
        'Sweden': 'SE',
        'Norway': 'NO',
        'Denmark': 'DK',
        'Finland': 'FI',
        'United States': 'US',
        'Canada': 'CA',
        'Australia': 'AU',
        'New Zealand': 'NZ',
        'Japan': 'JP',
        'Singapore': 'SG'
      };
      
      return countryMapping[countryName] || countryName;
    },
    async setupStripeConnect() {
      if (this.connectLoading) return;
      
      // If user doesn't have an account, show country selection first
      if (!this.connectAccountStatus.hasAccount) {
        await this.fetchSupportedCountries();
        this.showCountryModal = true;
        return;
      }
      
      // If account exists, proceed directly to onboarding
      this.createOnboardingLink();
    },
    async createConnectAccountWithCountry() {
      if (!this.selectedCountry) {
        alert("Please select your country.");
        return;
      }
      
      this.connectLoading = true;
      
      try {
        const token = localStorage.getItem("access_token");
        
        // Step 1: Create Connect account with selected country
        await axios.post(
          `${API_BASE_URL}/payments/connect/create-account/`,
          {
            country: this.selectedCountry
          },
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        
        // Close the modal
        this.showCountryModal = false;
        
        // Step 2: Get onboarding link
        this.createOnboardingLink();
        
      } catch (error) {
        console.error("Error creating Connect account:", error);
        const errorMessage = error.response?.data?.error || "Failed to create payout account. Please try again.";
        alert(errorMessage);
      } finally {
        this.connectLoading = false;
      }
    },
    async createOnboardingLink() {
      this.connectLoading = true;
      
      try {
        const token = localStorage.getItem("access_token");
        
        const linkResponse = await axios.post(
          `${API_BASE_URL}/payments/connect/onboarding-link/`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        
        // Redirect to Stripe onboarding
        // Note: The backend should configure the return_url to redirect back to:
        // https://6buoys.com/connect/return
        if (linkResponse.data.onboarding_url) {
          window.location.href = linkResponse.data.onboarding_url;
        }
        
      } catch (error) {
        console.error("Error creating onboarding link:", error);
        alert("Failed to create onboarding link. Please try again.");
      } finally {
        this.connectLoading = false;
      }
    },
    dismissConnectBanner() {
      this.showConnectBanner = false;
    },
    closeCountryModal() {
      this.showCountryModal = false;
      this.selectedCountry = '';
      this.countrySearchQuery = '';
    },
    selectCountry(countryCode) {
      this.selectedCountry = countryCode;
    },
    async fetchPayoutData() {
      this.payoutLoading = true;
      try {
        const token = localStorage.getItem("access_token");
        const response = await axios.get(
          `${API_BASE_URL}/payments/earnings/pending/`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        
        this.payoutData = {
          pendingAmount: response.data.pending_amount || 0,
          pendingPaymentsCount: response.data.pending_payments_count || 0,
          hasConnectAccount: response.data.has_connect_account || false,
          connectReady: response.data.connect_ready || false
        };
        
      } catch (error) {
        console.error("Error fetching payout data:", error);
      } finally {
        this.payoutLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.pro-tip-requests {
  max-width: 600px;
  margin: 0 auto;
  padding: 20px;
  color: #d9d9d9;
  margin-bottom: 80px;
}

.back-link-section {
  text-align: center;
  margin-top: 20px;
  margin-bottom: 20px;
}

.back-link {
  color: #ffffff;
  text-decoration: none;
  font-weight: bold;
}

.arrow {
  font-size: 1.5em;
  margin-right: 8px;
  vertical-align: middle;
}

.page-title {
  text-align: center;
  color: #1da1f2;
  margin-bottom: 20px;
}

.no-requests-message {
  text-align: center;
  padding: 20px;
  background-color: #192734;
  border-radius: 12px;
}

.tip-requests-list {
  padding-inline-start: 0px;
}

.request-item {
  display: flex;
  flex-direction: column;
  gap: 12px;
  background-color: #192734;
  margin-bottom: 16px;
  padding: 16px;
  border-radius: 12px;
  list-style-type: none;
}

/* First row styling: Profile picture on left, status and price on right */
.first-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.user-avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
}

.payment-details {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  text-align: right;
}

.payment-status,
.payment-price {
  font-weight: bold;
  color: #1da1f2;
}

.post-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.post-media {
  width: 100%;
  max-height: 300px;
  object-fit: cover;
  border-radius: 8px;
}

.post-text {
  color: #d9d9d9;
  text-align: left;
  width: 100%;
}

.add-tip-form {
  display: flex;
  flex-direction: column;
  margin-top: 8px;
}

.tip-textarea {
  width: 95%;
  min-height: 100px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #38444d;
  background-color: #15202b;
  color: #e1e8ed;
  resize: vertical;
  font-size: 16px;
  font-family: inherit;
}

.submit-tip-btn {
  padding: 10px 16px;
  background-color: #1da1f2;
  color: #ffffff;
  border: none;
  border-radius: 24px;
  cursor: pointer;
  font-weight: bold;
  align-self: flex-end;
  margin-top: 12px;
}

.submit-tip-btn:hover {
  background-color: #0c8ddb;
}

.submit-tip-btn:disabled {
  background-color: #38444d;
  cursor: not-allowed;
}

/* New styles for toggle and payment overview */
.view-toggle {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
  gap: 10px;
  flex-wrap: wrap;
}

.toggle-btn {
  padding: 10px 20px;
  background-color: #192734;
  color: #d9d9d9;
  border: none;
  border-radius: 24px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s ease;
  min-width: 120px;
}

.toggle-btn.active {
  background-color: #1da1f2;
  color: white;
}

.toggle-btn:hover:not(.active) {
  background-color: #38444d;
}

.payment-overview {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.payment-stats {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.stat-card {
  flex: 1;
  min-width: 150px;
  background-color: #192734;
  padding: 16px;
  border-radius: 12px;
  text-align: center;
  box-sizing: border-box;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #1da1f2;
  margin-top: 8px;
}

.chart-container {
  background-color: #192734;
  padding: 16px;
  border-radius: 12px;
  height: 300px;
}

.chart-container h3 {
  margin-top: 0;
  margin-bottom: 16px;
  text-align: center;
}

.recent-payments {
  background-color: #192734;
  padding: 16px;
  border-radius: 12px;
}

.recent-payments h3 {
  margin-top: 0;
  margin-bottom: 16px;
}

.payments-table {
  width: 100%;
  border-collapse: collapse;
}

.payments-table th, 
.payments-table td {
  padding: 10px;
  text-align: left;
  border-bottom: 1px solid #38444d;
}

.payments-table th {
  color: #1da1f2;
}

.no-data {
  text-align: center;
  padding: 20px;
  color: #8899a6;
}

@media screen and (max-width: 600px) {
  .payment-stats {
    flex-direction: column;
    gap: 12px;
  }
  
  .stat-card {
    width: 100%;
    min-width: unset;
    max-width: 100%;
    padding: 10px;
    margin: 0;
    flex: none;
  }
  
  .stat-value {
    font-size: 20px;
    margin-top: 4px;
  }
  
  .payments-table {
    font-size: 14px;
  }

  .pro-tip-requests {
    padding: 15px 10px;
  }

  .chart-container {
    padding: 12px;
    height: 250px;
  }

  .recent-payments {
    padding: 12px;
    overflow-x: auto;
  }

  /* Connect banner mobile styles */
  .connect-banner {
    padding: 12px;
  }

  .connect-banner-content {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }

  .connect-banner-text h4 {
    font-size: 16px;
  }

  .connect-banner-text p {
    font-size: 13px;
  }

  .connect-banner-actions {
    width: 100%;
    justify-content: space-between;
  }

  .connect-setup-btn {
    flex: 1;
    padding: 12px 16px;
  }

  /* Modal mobile styles */
  .modal-content {
    width: 95%;
    max-height: 90vh;
  }

  .modal-header {
    padding: 16px;
  }

  .modal-header h3 {
    font-size: 18px;
  }

  .modal-body {
    padding: 16px;
    max-height: 300px;
  }

  .modal-footer {
    padding: 16px;
    flex-direction: column;
    gap: 8px;
  }

  .modal-cancel-btn,
  .modal-confirm-btn {
    width: 100%;
    padding: 12px 20px;
  }

  .country-item {
    padding: 16px;
  }

  .country-code {
    font-size: 11px;
  }

  /* Payouts mobile styles */
  .payout-status-cards {
    flex-direction: column;
    gap: 12px;
  }

  .status-card {
    padding: 12px;
  }

  .card-header h3 {
    font-size: 14px;
  }

  .amount {
    font-size: 20px;
  }

  .connect-status-section {
    padding: 12px;
  }

  .pending-earnings-info {
    padding: 12px;
  }

  .info-content {
    align-items: flex-start;
  }

  .info-item {
    align-items: flex-start;
    gap: 6px;
  }

  .info-item span {
    font-size: 13px;
  }

  /* View toggle mobile styles */
  .view-toggle {
    flex-direction: column;
    gap: 8px;
  }

  .toggle-btn {
    min-width: unset;
    width: 100%;
    padding: 12px 16px;
  }
}

.pro-tips-info {
  margin-bottom: 12px;
}

.info-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: 8px 0;
}

.info-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-text {
  font-size: 14px;
  font-weight: 600;
  color: #1da1f2;
}

.info-icon {
  color: #1da1f2;
  font-size: 16px;
  transition: transform 0.3s ease;
}

.info-icon.active {
  transform: rotate(180deg);
}

.character-counter {
  font-size: 12px;
  color: #8899a6;
  font-weight: 500;
  padding: 4px 8px;
  background-color: #15202b;
  border-radius: 12px;
  border: 1px solid #38444d;
}

.info-content {
  background-color: #15202b;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
  border: 1px solid #38444d;
  text-align: left;
}

.info-list {
  margin: 0;
  padding-left: 16px;
  color: #d9d9d9;
}

.info-list li {
  margin-bottom: 8px;
  line-height: 1.4;
  font-size: 14px;
}

.info-list li:last-child {
  margin-bottom: 0;
}

.connect-banner {
  background: linear-gradient(135deg, #1da1f2 0%, #0c7abf 100%);
  padding: 16px;
  border-radius: 12px;
  margin-bottom: 20px;
  border: 1px solid rgba(29, 161, 242, 0.3);
}

.connect-banner-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.connect-banner-icon {
  font-size: 24px;
  color: #ffffff;
  flex-shrink: 0;
}

.connect-banner-text {
  flex: 1;
  min-width: 0;
}

.connect-banner-text h4 {
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: bold;
  color: #ffffff;
}

.connect-banner-text p {
  margin: 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.4;
}

.connect-banner-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.connect-setup-btn {
  padding: 10px 16px;
  background-color: #ffffff;
  color: #1da1f2;
  border: none;
  border-radius: 24px;
  cursor: pointer;
  font-weight: bold;
  font-size: 14px;
  transition: all 0.3s ease;
}

.connect-setup-btn:hover:not(:disabled) {
  background-color: #f0f8ff;
  transform: translateY(-1px);
}

.connect-setup-btn:disabled {
  background-color: rgba(255, 255, 255, 0.3);
  color: rgba(255, 255, 255, 0.7);
  cursor: not-allowed;
}

.connect-dismiss-btn {
  background: none;
  border: none;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.connect-dismiss-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: #ffffff;
}

/* Country Selection Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.modal-content {
  background-color: #192734;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #38444d;
}

.modal-header h3 {
  margin: 0;
  color: #1da1f2;
  font-size: 20px;
}

.modal-close-btn {
  background: none;
  border: none;
  font-size: 18px;
  color: #8899a6;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.modal-close-btn:hover {
  background-color: #38444d;
  color: #d9d9d9;
}

.modal-body {
  padding: 20px;
  max-height: 400px;
  overflow-y: auto;
}

.modal-description {
  margin: 0 0 20px 0;
  color: #d9d9d9;
  line-height: 1.5;
}

.loading-countries {
  text-align: center;
  padding: 20px;
  color: #8899a6;
}

.countries-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.country-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background-color: #15202b;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.country-item:hover {
  background-color: #1e2732;
}

.country-item.selected {
  background-color: #1da1f2;
  color: #ffffff;
}

.country-name {
  font-weight: 500;
  color: inherit;
}

.country-code {
  font-size: 12px;
  color: inherit;
  opacity: 0.8;
  font-family: monospace;
  background-color: rgba(255, 255, 255, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px;
  border-top: 1px solid #38444d;
}

.modal-cancel-btn {
  padding: 10px 20px;
  background-color: transparent;
  color: #8899a6;
  border: 1px solid #38444d;
  border-radius: 24px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
}

.modal-cancel-btn:hover {
  background-color: #38444d;
  color: #d9d9d9;
}

.modal-confirm-btn {
  padding: 10px 20px;
  background-color: #1da1f2;
  color: #ffffff;
  border: none;
  border-radius: 24px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s ease;
}

.modal-confirm-btn:hover:not(:disabled) {
  background-color: #0c7abf;
}

.modal-confirm-btn:disabled {
  background-color: #38444d;
  color: #8899a6;
  cursor: not-allowed;
}

.country-search {
  margin-bottom: 16px;
}

.search-input {
  width: 100%;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #38444d;
  background-color: #15202b;
  color: #e1e8ed;
}

.no-countries {
  text-align: center;
  padding: 20px;
  color: #8899a6;
}

.payouts-overview {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.payout-status-cards {
  display: flex;
  justify-content: space-between;
  gap: 16px;
}

.status-card {
  flex: 1;
  background-color: #192734;
  padding: 16px;
  border-radius: 12px;
  text-align: center;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.card-header h3 {
  margin: 0;
  color: #1da1f2;
}

.card-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.amount {
  font-size: 24px;
  font-weight: bold;
  color: #1da1f2;
  margin-bottom: 4px;
}

.count {
  font-size: 14px;
  color: #8899a6;
}

.connect-status-section {
  background-color: #192734;
  padding: 16px;
  border-radius: 12px;
}

.connect-status {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
}

.connect-status .status-icon {
  font-size: 24px;
  color: #1da1f2;
}

.connect-status .status-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.connect-status .status-title {
  font-size: 16px;
  font-weight: bold;
  color: #1da1f2;
  margin-bottom: 4px;
}

.connect-status .status-description {
  font-size: 14px;
  color: #8899a6;
}

.connect-status .setup-connect-btn {
  padding: 10px 16px;
  background-color: #1da1f2;
  color: #ffffff;
  border: none;
  border-radius: 24px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s ease;
}

.connect-status .setup-connect-btn:hover:not(:disabled) {
  background-color: #0c7abf;
}

.connect-status .setup-connect-btn:disabled {
  background-color: #38444d;
  color: #8899a6;
  cursor: not-allowed;
}

.connect-status .refresh-btn {
  padding: 10px 16px;
  background: none;
  border: none;
  font-size: 14px;
  color: #8899a6;
  cursor: pointer;
  transition: all 0.3s ease;
}

.connect-status .refresh-btn:hover {
  color: #1da1f2;
}

.connect-status .status-badge {
  padding: 4px 8px;
  background-color: #1da1f2;
  color: #ffffff;
  border-radius: 12px;
  font-size: 12px;
  font-weight: bold;
}

.pending-earnings-info {
  background-color: #192734;
  padding: 16px;
  border-radius: 12px;
}

.info-header {
  margin-bottom: 16px;
}

.info-header h3 {
  margin: 0;
  color: #1da1f2;
  font-size: 16px;
  font-weight: bold;
}

.info-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-item i {
  color: #1da1f2;
  font-size: 16px;
}

.info-item span {
  font-size: 14px;
  color: #d9d9d9;
}

.no-earnings {
  text-align: center;
  padding: 20px;
  background-color: #192734;
  border-radius: 12px;
}

.no-earnings-icon {
  font-size: 24px;
  color: #1da1f2;
  margin-bottom: 8px;
}

.no-earnings h3 {
  margin: 0;
  font-size: 16px;
  color: #8899a6;
}

.no-earnings p {
  margin: 0;
  font-size: 14px;
  color: #8899a6;
}
</style> 